<div class="finance-list-page" *ngIf="canView">
  <app-page-shell>
    <p-toast position="top-center"></p-toast>
    <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

    <app-page-hero eyebrow="Payables" title="Bulk Costs"
      subtitle="Review operational cost batches from shipments, order releases, clearance, projects, and manual supplier invoices before posting.">
      <div actions>
        <p-button label="Account Defaults" icon="pi pi-cog" severity="secondary" [outlined]="true"
          (click)="openAccountDefaults()" *ngIf="canEdit"></p-button>
        <p-button label="Import Source Costs" icon="pi pi-download" severity="secondary" [outlined]="true"
          (click)="openSourceImport()" *ngIf="canEdit"></p-button>
        <p-button label="Export to Excel" icon="pi pi-file-excel" severity="secondary" [outlined]="true"
          [loading]="isExporting" (click)="exportToExcel()" *ngIf="canExport"></p-button>
        <p-button label="New Bulk Cost" icon="pi pi-plus" (click)="openCreate()" *ngIf="canEdit"></p-button>
      </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-search-panel title="Search bulk costs" [activeFilters]="activeFilterCount"
      [resultText]="resultText" [collapsed]="false" (search)="applyFilters()" (clear)="clearFilters()">
      <div class="bulk-actions quick-filters">
        <p-button label="Unapproved" severity="secondary" [outlined]="!selectedStatuses.includes('UNAPPROVED')"
          (click)="selectedStatuses = ['UNAPPROVED']; applyFilters()"></p-button>
        <p-button label="Approved" severity="secondary" [outlined]="!selectedStatuses.includes('APPROVED')"
          (click)="selectedStatuses = ['APPROVED']; applyFilters()"></p-button>
        <p-button label="Posted" severity="secondary" [outlined]="!selectedStatuses.includes('POSTED')"
          (click)="selectedStatuses = ['POSTED']; applyFilters()"></p-button>
      </div>

      <div class="finance-search-grid">
        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="bulkCostKeyword" pInputText [(ngModel)]="keyword" />
              <label for="bulkCostKeyword">Search</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>

        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-datePicker inputId="bulkCostDateRange" [(ngModel)]="dateRange" selectionMode="range"
                [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
              <label for="bulkCostDateRange">Posting Date Range</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>

        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-multiSelect inputId="bulkCostStatus" [options]="statusOptions" [(ngModel)]="selectedStatuses"
                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
              <label for="bulkCostStatus">Status</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>

        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-multiSelect inputId="bulkCostPostingStatus" [options]="postingStatusOptions"
                [(ngModel)]="selectedPostingStatuses" display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
              <label for="bulkCostPostingStatus">Posting Status</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>
      </div>
    </app-page-search-panel>

    <app-page-data-panel title="Bulk Cost Queue" subtitle="Supplier and internal cost batches ready for finance review."
      [resultText]="resultText">
      <div class="bulk-action-bar" *ngIf="selectedRows.length">
        <div>
          <strong>{{ selectedRows.length }}</strong> selected,
          <strong>{{ formatMoney(selectedTotal()) }}</strong>
        </div>
        <div class="bulk-actions">
          <p-button label="Export Selected" icon="pi pi-file-excel" severity="secondary" [outlined]="true"
            [loading]="isExporting" (click)="exportToExcel()" *ngIf="canExport"></p-button>
          <p-button label="Approve Selected" icon="pi pi-check" severity="success" [outlined]="true"
            *ngIf="canEdit" (click)="bulkApprove()"></p-button>
        </div>
      </div>

      <p-table class="finance-table bulk-cost-table" [value]="rows" dataKey="bulkCostId" [paginator]="true"
        [rows]="pageSize" [first]="first" [totalRecords]="totalRecords" [lazy]="true"
        [rowsPerPageOptions]="[25, 50, 100]" [(selection)]="selectedRows" [loading]="isLoading"
        [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        (onLazyLoad)="loadData($event)" [tableStyle]="{ 'min-width': '1190px' }">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 3.5rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
            <th>Row No</th>
            <th>Supplier / Source</th>
            <th>Delivery / Assets</th>
            <th>Currency</th>
            <th>Invoice No</th>
            <th>Invoice Date</th>
            <th>Excl. Tax</th>
            <th>VAT</th>
            <th>Local Amount</th>
            <th>Status</th>
            <th>Approved / Posted</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td data-label="Select"><p-tableCheckbox [value]="row"></p-tableCheckbox></td>
            <td data-label="Row No">
              <button type="button" class="cell-title link-button" (click)="openView(row)">
                {{ row.bulkCostNumber || ('BC-' + row.bulkCostId) }}
              </button>
              <div class="cell-meta">{{ row.lines?.length || 0 }} lines</div>
            </td>
            <td data-label="Supplier / Source">
              <div class="stack-cell">
                <span class="cell-title">{{ row.supplierName || 'Internal / Source Cost' }}</span>
                <div class="cell-meta"><span class="code-pill">{{ row.sourceType || 'MANUAL' }}</span></div>
              </div>
            </td>
            <td data-label="Delivery / Assets">
              <div class="stack-cell" *ngIf="row.deliveryNumber || row.driverName || row.vehicleLabel || row.logisticsProjectNumber; else noDeliveryContext">
                <span class="cell-title">{{ row.deliveryNumber || row.shipmentNumber || 'Delivery pending' }}</span>
                <span class="cell-meta" *ngIf="row.logisticsProjectNumber">{{ row.logisticsProjectNumber }}</span>
                <div class="cell-meta" *ngIf="row.driverName || row.vehicleLabel">
                  <span *ngIf="row.driverName"><i class="pi pi-user"></i> {{ row.driverName }}</span>
                  <span *ngIf="row.vehicleLabel"><i class="pi pi-truck"></i> {{ row.vehicleLabel }}</span>
                </div>
              </div>
              <ng-template #noDeliveryContext>-</ng-template>
            </td>
            <td data-label="Currency">
              <div class="stack-cell">
                <span>{{ row.currencyCode || 'SAR' }}</span>
                <span class="cell-meta">{{ row.exchangeRate || 1 | number:'1.4-4' }}</span>
              </div>
            </td>
            <td data-label="Invoice No">{{ row.invoiceNumber || '-' }}</td>
            <td data-label="Invoice Date">{{ row.invoiceDate | date:'yyyy-MM-dd' }}</td>
            <td data-label="Excl. Tax">{{ formatMoney(row.grossAmount, row.currencyCode) }}</td>
            <td data-label="VAT">{{ formatMoney(row.taxAmount, row.currencyCode) }}</td>
            <td data-label="Local Amount">{{ formatMoney(row.localAmount, row.currencyCode) }}</td>
            <td data-label="Status">
              <div class="status-stack">
                <p-tag [value]="row.status || 'UNAPPROVED'" [severity]="statusSeverity(row.status)"></p-tag>
                <span class="muted-pill">{{ row.postingStatus || 'NOT_POSTED' }}</span>
              </div>
            </td>
            <td data-label="Approved / Posted">
              <div class="cell-meta vertical">
                <span>Approved {{ row.approvedAt ? (row.approvedAt | date:'yyyy-MM-dd HH:mm') : '-' }}</span>
                <span>Posted {{ row.postedAt ? (row.postedAt | date:'yyyy-MM-dd HH:mm') : '-' }}</span>
                <span *ngIf="row.journalEntryId">JE #{{ row.journalEntryId }}</span>
              </div>
            </td>
            <td data-label="Actions">
              <div class="row-actions">
                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" pTooltip="Open bulk cost"
                  (click)="openView(row)"></p-button>
                <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary" pTooltip="Edit"
                  *ngIf="canEdit && row.postingStatus !== 'POSTED'" (click)="openEdit(row)"></p-button>
                <p-button icon="pi pi-check" [rounded]="true" [text]="true" severity="success" pTooltip="Approve"
                  *ngIf="canEdit && row.status === 'UNAPPROVED'" (click)="approve(row)"></p-button>
                <p-button icon="pi pi-send" [rounded]="true" [text]="true" severity="help" pTooltip="Post"
                  *ngIf="canPost && row.status === 'APPROVED' && row.postingStatus !== 'POSTED'" (click)="post(row)"></p-button>
                <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="danger" pTooltip="Cancel"
                  *ngIf="canEdit && row.postingStatus !== 'POSTED' && row.status !== 'CANCELLED'" (click)="cancel(row)"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="13">
              <div class="empty-state">
                <i class="pi pi-inbox"></i>
                <strong>No bulk costs match these filters</strong>
                <span>Create a bulk cost or pull source rows from operational modules.</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </app-page-shell>
</div>

<div class="finance-list-page" *ngIf="!canView">
  <p-toast position="top-center"></p-toast>
  <div class="empty-state">
    <i class="pi pi-lock"></i>
    <strong>Bulk Costs are not available</strong>
    <span>You do not have permission to view payables.</span>
  </div>
</div>

<p-dialog [(visible)]="sourceDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  styleClass="source-cost-dialog" [style]="{ width: 'min(980px, 94vw)' }" header="Import Source Costs">
  <div class="source-dialog-header">
    <div>
      <strong>Shipment internal cost allocations</strong>
      <span>Select generated operational cost rows to create one finance-reviewed bulk cost.</span>
    </div>
    <div class="source-dialog-actions">
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true"
        (click)="loadSourceCandidates()"></p-button>
    </div>
  </div>

  <p-table [value]="sourceCandidates" dataKey="allocationId" [(selection)]="selectedSourceCandidates"
    [loading]="isSourceLoading" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '820px' }">
    <ng-template pTemplate="header">
      <tr>
        <th style="width: 3.5rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
        <th>Source</th>
        <th>Shipment / Delivery</th>
        <th>Basis</th>
        <th>Effective</th>
        <th class="text-right">Amount</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row>
      <tr>
        <td><p-tableCheckbox [value]="row"></p-tableCheckbox></td>
        <td>
          <div class="stack-cell">
            <span class="cell-title">{{ row.sourceLabel || row.sourceType }}</span>
            <span class="cell-meta">Allocation #{{ row.allocationId || row.sourceDetailId }}</span>
          </div>
        </td>
        <td>
          <div class="stack-cell">
            <span class="cell-title">{{ row.shipmentNumber || row.jobReference || '-' }}</span>
            <span class="cell-meta">{{ row.deliveryNumber || 'Shipment level' }}</span>
          </div>
        </td>
        <td>{{ row.unit || '-' }}</td>
        <td>{{ row.effectiveDate | date:'yyyy-MM-dd' }}</td>
        <td class="text-right">{{ formatMoney(row.localAmount || row.amount, row.currencyCode || 'SAR') }}</td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="6">
          <div class="empty-state compact">
            <i class="pi pi-check-circle"></i>
            <strong>No pending source costs</strong>
            <span>Generated shipment cost allocations already imported will not be shown again.</span>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>

  <div class="source-dialog-footer">
    <span>{{ selectedSourceCandidates.length }} selected, {{ formatMoney(sourceSelectedTotal(), sourceSelectedCurrency()) }}</span>
    <div>
      <p-button label="Cancel" severity="secondary" [text]="true" (click)="sourceDialogVisible = false"></p-button>
      <p-button label="Create Bulk Cost" icon="pi pi-file-plus" [loading]="isSaving" (click)="createFromSources()"></p-button>
    </div>
  </div>
</p-dialog>

<p-dialog [(visible)]="editorVisible" [modal]="true" [draggable]="false" [resizable]="false"
  styleClass="bulk-cost-editor" [style]="{ width: 'min(1180px, 96vw)' }"
  [header]="editing.bulkCostNumber ? 'Bulk Cost - ' + editing.bulkCostNumber : 'Bulk Cost'">
  <div class="editor-grid">
    <div class="field">
      <p-iftalabel>
        <input id="supplierName" pInputText [(ngModel)]="editing.supplierName" />
        <label for="supplierName">Supplier / Internal Party</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <input id="invoiceNumber" pInputText [(ngModel)]="editing.invoiceNumber" />
        <label for="invoiceNumber">Invoice No</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <p-select inputId="sourceType" [options]="sourceTypeOptions" [(ngModel)]="editing.sourceType" styleClass="w-full"></p-select>
        <label for="sourceType">Source Type</label>
      </p-iftalabel>
    </div>
    <div class="field currency-field">
      <p-iftalabel>
        <p-inputNumber inputId="exchangeRate" [(ngModel)]="editing.exchangeRate" [minFractionDigits]="4" [maxFractionDigits]="6"
          styleClass="w-full"></p-inputNumber>
        <label for="exchangeRate">Currency Exchange Rate</label>
      </p-iftalabel>
      <p-iftalabel>
        <input id="currencyCode" pInputText [(ngModel)]="editing.currencyCode" maxlength="3" />
        <label for="currencyCode">Currency</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <p-datePicker inputId="invoiceDate" [(ngModel)]="editing.invoiceDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
        <label for="invoiceDate">Invoice Date</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <p-datePicker inputId="invoiceReceivedDate" [(ngModel)]="editing.invoiceReceivedDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
        <label for="invoiceReceivedDate">Invoice Received Date</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <p-datePicker inputId="postingDate" [(ngModel)]="editing.postingDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
        <label for="postingDate">Posting Date</label>
      </p-iftalabel>
    </div>
    <div class="field">
      <p-iftalabel>
        <p-select inputId="payableAccountId" [options]="accounts" [(ngModel)]="editing.payableAccountId"
          optionLabel="accountName" optionValue="accountId" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
        <label for="payableAccountId">Expense Account</label>
      </p-iftalabel>
    </div>
  </div>

  <div class="automatic-header-context" *ngIf="editing.sourceType === 'SHIPMENT_INTERNAL_COST' && (editing.deliveryNumber || editing.driverName || editing.vehicleLabel || editing.logisticsProjectNumber)">
    <div>
      <span>Delivery</span>
      <strong>{{ editing.deliveryNumber || 'Pending delivery' }}</strong>
    </div>
    <div>
      <span>Logistics Project</span>
      <strong>{{ projectContextLabel(editing, 'Multiple projects on cost lines') }}</strong>
    </div>
    <div>
      <span>Driver</span>
      <strong>{{ editing.driverName || 'Unassigned' }}</strong>
    </div>
    <div>
      <span>Vehicle</span>
      <strong>{{ editing.vehicleLabel || 'Unassigned' }}</strong>
    </div>
  </div>

  <div class="editor-toggle-row">
    <div>
      <strong>Include Tax</strong>
      <span>When enabled, local totals include line tax during posting.</span>
    </div>
    <p-checkbox [(ngModel)]="editing.includeTax" [binary]="true"></p-checkbox>
  </div>

  <div class="bulk-cost-lines">
    <div class="line-toolbar">
      <div>
        <strong>Cost Lines</strong>
        <span>{{ editing.lines?.length || 0 }} lines</span>
      </div>
      <p-button label="Add Line" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="addLine()"></p-button>
    </div>

    <div class="bulk-cost-edit-lines" role="list">
      <div class="bulk-cost-edit-line" role="listitem" *ngFor="let line of editing.lines || []; let rowIndex = index">
        <div class="bulk-cost-line-header">
          <strong>Line {{ rowIndex + 1 }}</strong>
          <div class="row-actions">
            <p-button icon="pi pi-copy" [rounded]="true" [text]="true" [ariaLabel]="'Duplicate line ' + (rowIndex + 1)" (click)="duplicateLine(line)"></p-button>
            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" [ariaLabel]="'Remove line ' + (rowIndex + 1)" (click)="removeLine(rowIndex)"></p-button>
          </div>
        </div>
        <div class="bulk-cost-line-primary">
          <div class="bulk-cost-line-field"><label [for]="'cost-job-' + rowIndex">Job</label><input [id]="'cost-job-' + rowIndex" pInputText [(ngModel)]="line.jobReference" placeholder="Job / shipment"></div>
          <div class="bulk-cost-line-field"><label [for]="'cost-description-' + rowIndex">Description</label><input [id]="'cost-description-' + rowIndex" pInputText [(ngModel)]="line.description" placeholder="Description"></div>
          <div class="bulk-cost-line-field"><label [for]="'cost-account-' + rowIndex">Account</label><p-select [inputId]="'cost-account-' + rowIndex" [options]="accounts" [(ngModel)]="line.accountId" optionLabel="accountName" optionValue="accountId" [filter]="true" appendTo="body" styleClass="w-full"></p-select></div>
          <div class="bulk-cost-line-field"><label [for]="'cost-qty-' + rowIndex">Qty</label><p-inputNumber [inputId]="'cost-qty-' + rowIndex" [(ngModel)]="line.quantity" [minFractionDigits]="2" [maxFractionDigits]="4" [inputStyle]="{ width: numberInputWidth(line.quantity, 8, 20, 2) }"></p-inputNumber></div>
          <div class="bulk-cost-line-field"><label [for]="'cost-price-' + rowIndex">Price</label><p-inputNumber [inputId]="'cost-price-' + rowIndex" [(ngModel)]="line.price" mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="4" [inputStyle]="{ width: numberInputWidth(line.price, 10, 24) }"></p-inputNumber></div>
          <div class="bulk-cost-line-field"><label [for]="'cost-tax-' + rowIndex">Tax</label><p-inputNumber [inputId]="'cost-tax-' + rowIndex" [(ngModel)]="line.taxAmount" mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="4" [inputStyle]="{ width: numberInputWidth(line.taxAmount, 10, 24) }"></p-inputNumber></div>
          <div class="bulk-cost-line-field"><span>Amount</span><strong class="bulk-cost-line-amount">{{ formatMoney(lineTotal(line), editing.currencyCode) }}</strong></div>
        </div>
        <div class="bulk-cost-line-secondary">
          <div class="bulk-cost-line-field"><label [for]="'cost-unit-' + rowIndex">Unit</label><input [id]="'cost-unit-' + rowIndex" pInputText [(ngModel)]="line.unit" placeholder="Unit"></div>
          <div class="bulk-cost-line-field bulk-cost-line-comments"><label [for]="'cost-comments-' + rowIndex">Comments</label><textarea [id]="'cost-comments-' + rowIndex" pInputTextarea [(ngModel)]="line.comments" rows="1"></textarea></div>
        </div>
      </div>
    </div>
  </div>

  <div class="editor-totals">
    <div><span>Total Amount</span><strong>{{ formatMoney(totalAmount(), editing.currencyCode) }}</strong></div>
    <div><span>Total Tax Amount</span><strong>{{ formatMoney(totalTax(), editing.currencyCode) }}</strong></div>
    <div><span>Local Amount</span><strong>{{ formatMoney(totalLocal(), editing.currencyCode) }}</strong></div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [text]="true" (click)="editorVisible = false"></p-button>
    <p-button label="Save" icon="pi pi-save" [loading]="isSaving" (click)="save()"></p-button>
  </ng-template>
</p-dialog>

<p-drawer [(visible)]="previewVisible" position="right" styleClass="finance-preview-drawer bulk-cost-preview">
  <ng-template pTemplate="header">
    <div class="stack-cell">
      <strong>{{ preview?.bulkCostNumber || 'Bulk cost' }}</strong>
      <span class="cell-meta">{{ preview?.supplierName || preview?.sourceType || 'Cost document' }}</span>
    </div>
  </ng-template>
  <div class="preview-summary">
    <p-tag [value]="preview?.status || 'UNAPPROVED'" [severity]="statusSeverity(preview?.status)"></p-tag>
    <strong>{{ formatMoney(preview?.localAmount, preview?.currencyCode) }}</strong>
    <span>Invoice {{ preview?.invoiceNumber || '-' }}</span>
  </div>
  <div class="preview-context" *ngIf="preview?.deliveryNumber || preview?.driverName || preview?.vehicleLabel || preview?.logisticsProjectNumber">
    <span *ngIf="preview?.deliveryNumber"><i class="pi pi-send"></i> {{ preview?.deliveryNumber }}</span>
    <span *ngIf="preview?.logisticsProjectNumber"><i class="pi pi-briefcase"></i> {{ preview?.logisticsProjectNumber }}</span>
    <span *ngIf="preview?.driverName"><i class="pi pi-user"></i> {{ preview?.driverName }}</span>
    <span *ngIf="preview?.vehicleLabel"><i class="pi pi-truck"></i> {{ preview?.vehicleLabel }}</span>
  </div>
  <div class="preview-list">
    <div class="preview-line" *ngFor="let line of preview?.lines">
      <div class="preview-line-header">
        <div>
          <div class="preview-line-title">{{ line.jobReference || line.shipmentNumber || 'Line item' }}</div>
          <div class="cell-meta">{{ line.description || line.sourceType }}</div>
        </div>
        <div class="preview-line-amount">{{ formatMoney(line.localAmount || line.amount, preview?.currencyCode) }}</div>
      </div>
      <button type="button" class="cell-title link-button" *ngIf="line.shipmentId || line.deliveryId" (click)="viewSource(line)">
        Open source
      </button>
    </div>
  </div>
</p-drawer>
